import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';

import * as locales from 'date-fns/locale';

import {Group} from '@pb/kit-component-group';
import {InfoMessage} from '@pb/kit-component-message';
import {Tooltip} from '@pb/kit-component-popups';

import type {CalendarCoreProps} from './calendar-core';
import {CalendarCore} from './calendar-core';
import {CalendarInput} from './calendar-input';
import {defaultTodayButton} from './default-today-button';
import {CustomCalendarHeader} from './custom-calendar-header';
import {formatValidDateToString} from './utils/formatValidDateToString';
import {getDateRange} from './utils/getDateRange';
import {getEffectiveDateFormat} from './utils/getEffectiveDateFormat';

import type {ErrorType} from './constants';
import type {CustomHeaderProps} from './typedef';
import type {InputProps} from '@pb/kit-component-input-v2';
import type {Day, Locale} from 'date-fns';
import type {ComponentProps, ReactNode,
    ComponentType} from 'react';
import type DatePicker from 'react-datepicker';


export const DEFAULT_MIN_DATE: Date = new Date('1800-01-01');
export const DEFAULT_MAX_DATE: Date = new Date('2050-12-31');

const getLocaleObject = (localeName: string): Locale => {
    const locale = (locales as Record<string, Locale>)[localeName];
    if (!locale) {
        return locales.enUS;
    }
    return locale;
};

export interface OmittedInputProps extends Omit<InputProps, 'value'> {
    value?: string,
}

type DatePickerProps = Omit<ComponentProps<typeof DatePicker>, 'onChange' | 'locale' | 'onCalendarClose'>;

export interface CalendarProps extends DatePickerProps {
    /**
     * This value will be set when you press the clear button.
     */
    clearableValue?: Date,
    /**
     * Element custom header for calendar
     */
    CustomHeader?: ComponentType<CustomHeaderProps>,
    /**
     * Date format string following date-fns format patterns.
     * Common examples: 'MM-dd-yyyy', 'dd-MM-yyyy', 'yyyy-MM-dd', 'dd-MMM-yyyy', 'PPP', etc.
     * Default: 'dd-MMM-yyyy'
     * @see {@link https://date-fns.org/docs/format date-fns format documentation}
     */
    dateFormat?: CalendarCoreProps['dateFormatCalendar'],
    /**
     * Locale for datepicker
     */
    locale?: string,
    /**
     * Called before the calendar is closed.
     */
    onCalendarClose?: (date: Date | null) => void,
    /**
     * Called when the calendar has been changed.
     */
    onChange?: (date?: Date) => void,
    /**
     * Called when the calendar has been changed range.
     */
    onChangeRange?: (range: Array<Date | undefined>) => void,
    /**
     * Props for Input component
     */
    inputProps?: OmittedInputProps,
    /**
     * Add custom error message.
     *  @deprecated It will be removed in 6 months 09-15-2025. Use onValidationUpdate with external component FieldDescription.
     *  @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-form-fields-with-error--documentation}
     */
    setCustomErrorMessage?: (errorType: ErrorType, date: string) => string,
    /**
     *  Show error messages on date validation
     *  @deprecated It will be removed in 6 months 09-15-2025. Use onValidationUpdate with external component FieldDescription.
     *  @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-form-fields-with-error--documentation}
     */
    showErrorMessages?: boolean,
    /**
     * Callback to get an error message if the date is invalid.
     */
    onValidationUpdate?: (errorType?: ErrorType, errorMessage?: string) => void,
    /**
     * Show hint with information about min date and max date.
     */
    withRangeHint?: boolean,
    /**
     * Today button node
     */
    todayButton?: ReactNode,
    /**
     * Show time input under calendar.
     */
    isShownTimeInput?: boolean,
}

/**
 * @description The `Calendar` component is a versatile and customizable date picker component
 * that supports single date selection, date ranges, and week selection. It offers a wide range
 * of features to handle various date picking needs in an accessible and user-friendly manner.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/forms-calendar--calendar-default}
 */

export const Calendar = forwardRef<DatePicker, CalendarProps>(
    (props, ref) => {
        const {
            CustomHeader,
            clearableValue,
            dateFormat = 'MM-dd-yyyy',
            endDate: endDateProp,
            inline = false,
            inputProps,
            isClearable = false,
            locale = 'enUS',
            maxDate = DEFAULT_MAX_DATE,
            minDate = DEFAULT_MIN_DATE,
            onCalendarClose,
            onChange,
            onChangeRange,
            onKeyDown,
            popperPlacement = 'bottom-end',
            selected: selectedProp = null,
            setCustomErrorMessage,
            showErrorMessages = false,
            onValidationUpdate,
            showWeekPicker = false,
            startDate: startDateProp,
            withRangeHint = false,
            todayButton,
            isShownTimeInput = false,
            ...calendarProps
        } = props;

        const [selected, setSelected] = useState<Date | null>(selectedProp);
        const [startDate, setStartDate] = useState<Date | undefined>(startDateProp);
        const [endDate, setEndDate] = useState<Date | undefined>(endDateProp);
        const [infoMessage, setInfoMessage] = useState<string>('');

        const inputRef = useRef<HTMLInputElement | null>(null);
        const calendarRef = useRef<DatePicker | null>(null);

        useImperativeHandle(ref, () => calendarRef.current as DatePicker, []);

        useEffect(() => {
            setSelected(selectedProp);
        }, [selectedProp?.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            setStartDate(startDateProp);
        }, [startDateProp?.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            setEndDate(endDateProp);
        }, [endDateProp?.toString()]); // eslint-disable-line react-hooks/exhaustive-deps

        const localObject = getLocaleObject(locale);
        const weekStartsOn: Day = localObject.options?.weekStartsOn || 0;

        const setErrorMessage = (
            errorType: ErrorType | null,
            info: string,
            date: string,
        ) => {
            if (setCustomErrorMessage && errorType) {
                setInfoMessage(setCustomErrorMessage(errorType, date) || info);
            } else {
                setInfoMessage(info);
            }
        };

        const handleChange = (selectedDate: Date | Date[] | null) => {
            const isArray = Array.isArray(selectedDate);
            if (!selectedDate || (isArray && selectedDate.every(date => date === null))) {
                onClear();
                return;
            }

            if (showWeekPicker && !isArray) {
                const dateRange = getDateRange(selectedDate, weekStartsOn, dateFormat, true);
                onChangeRange?.(dateRange);
                setSelected(dateRange[0]);
            } else if (isArray) {
                const [startingDate, endingDate] = selectedDate;
                setStartDate(startingDate);
                setEndDate(endingDate);
                onChangeRange?.(selectedDate);
            } else if (!isArray) {
                setSelected(selectedDate);
                onChange?.(selectedDate);
            }
        };

        const handleCustomHeader = (customHeaderProps: CustomHeaderProps) => {
            const decreaseMonth = () => {
                customHeaderProps.decreaseMonth();
            };

            const increaseMonth = () => {
                customHeaderProps.increaseMonth();
            };

            const HeaderComponent = CustomHeader || CustomCalendarHeader;

            return (
                <HeaderComponent
                    {...customHeaderProps}
                    decreaseMonth={decreaseMonth}
                    increaseMonth={increaseMonth}
                    minDate={minDate}
                    maxDate={maxDate}
                    selectDate={handleChange}
                    selectedDate={selected}
                />
            );
        };

        const onClear = () => {
            if (calendarProps?.readOnly) {
                return;
            }
            setSelected(clearableValue ?? null);
            onChange?.(clearableValue ?? undefined);
            onChangeRange?.([undefined, undefined]);
            setStartDate(undefined);
            setEndDate(undefined);
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            onKeyDown?.(event);
            if (event.key === 'Escape') {
                // Workaround to fix the closing of the Dropdown on "Escape" key press
                event.stopPropagation();
            }
        };

        const calendarInput = (
            <CalendarInput
                {...inputProps}
                ref={inputRef}
                clearableValue={clearableValue}
                dateFormat={dateFormat}
                isClearable={isClearable}
                maxDate={maxDate}
                minDate={minDate}
                onClear={onClear}
                onDateChange={handleChange}
                setErrorMessage={setErrorMessage}
                showErrorMessages={showErrorMessages}
                onValidationUpdate={onValidationUpdate}
                showWeekPicker={showWeekPicker}
                weekStartsOn={weekStartsOn}
            />
        );

        // Determine which format to use for CalendarCore
        const effectiveDateFormat = useMemo(
            () => getEffectiveDateFormat(isShownTimeInput, dateFormat),
            [isShownTimeInput, dateFormat],
        );

        // Update placeholder if we're using dateTimeFormat
        const effectivePlaceholder = useMemo(() => {
            if (props.placeholderText) {
                return props.placeholderText;
            }

            return effectiveDateFormat?.toUpperCase() ?? '';
        }, [props.placeholderText, effectiveDateFormat]);

        const calendarCore = (
            <CalendarCore
                {...calendarProps}
                ref={calendarRef}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(props.showMonthYearDropdown && ({showMonthYearDropdown: true} as any))}
                customInput={calendarInput}
                dateFormat={effectiveDateFormat}
                endDate={endDate}
                inline={inline}
                locale={localObject}
                maxDate={maxDate}
                minDate={minDate}
                onCalendarClose={() => onCalendarClose?.(selected)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholderText={effectivePlaceholder}
                popperPlacement={popperPlacement}
                isShownTimeInput={isShownTimeInput}
                renderCustomHeader={handleCustomHeader}
                selected={selected}
                showWeekPicker={showWeekPicker}
                startDate={startDate}
                todayButton={todayButton ?? defaultTodayButton({selected, maxDate, minDate})}
            />
        );

        const tooltipCaption = (
            <Group justify="space-between">
                <div>
                    <strong>Min Date:</strong> {formatValidDateToString(minDate, dateFormat)};
                </div>
                <div>
                    <strong>Max Date:</strong> {formatValidDateToString(maxDate, dateFormat)}
                </div>
            </Group>
        );

        return (
            <>
                {withRangeHint ? (
                    <Tooltip
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span
                                ref={anchorRef}
                                {...getAnchorProps()}>{calendarCore}
                            </span>
                        )}
                        placement="right">
                        {tooltipCaption}
                    </Tooltip>
                ) : (
                    calendarCore
                )}
                {infoMessage ? (
                    <InfoMessage
                        shown={true}
                        onClose={() => setInfoMessage('')}>
                        {infoMessage}
                    </InfoMessage>
                ) : null}
            </>
        );
    },
);

Calendar.displayName = 'Calendar';
